export const APP_ROLES = [
  "Administrator",
  "Superuser",
  "Coordinator",
  "Viewer",
] as const;

export type AppRole = (typeof APP_ROLES)[number];

export const ACCESS_VALUES = ["R/W/D", "R/W", "R", "N"] as const;
export type AccessValue = (typeof ACCESS_VALUES)[number];
export type AccessAction = "read" | "write" | "delete";

export interface AccessRightRow {
  id: string;
  label: string;
  subtitle?: string;
  permissions: Record<AppRole, AccessValue>;
}

export interface AccessLevelDefinition {
  level: "None" | "Read" | "Write" | "Delete";
  definition: string;
  example: string;
}

export interface RoleUserTypes {
  role: AppRole;
  userTypes: string[];
}

export interface AuthorizationCapabilities {
  userId: string;
  email: string;
  role: AppRole;
  permissions: Partial<Record<AccessRightRow["id"], AccessValue>>;
}

export const ACCESS_RIGHT_ROWS: AccessRightRow[] = [
  {
    id: "admin-settings",
    label: "Admin Settings",
    permissions: {
      Administrator: "R/W/D",
      // Super users may view every Governance & Access area read-only; editing
      // stays gated by canWrite (except Care Team Members, which they own).
      Superuser: "R",
      Coordinator: "R",
      Viewer: "N",
    },
  },
  {
    id: "care-team-members",
    label: "Care Team Members",
    permissions: {
      Administrator: "R/W/D",
      Superuser: "R/W/D",
      Coordinator: "R",
      Viewer: "N",
    },
  },
  {
    id: "reports",
    label: "Reports",
    permissions: {
      Administrator: "R/W/D",
      Superuser: "R",
      Coordinator: "R",
      Viewer: "R",
    },
  },
  {
    id: "person-record",
    label: "Person Record",
    permissions: {
      Administrator: "R/W/D",
      Superuser: "R/W",
      Coordinator: "R",
      Viewer: "R",
    },
  },
  {
    id: "program-details",
    label: "Program Details",
    subtitle: "e.g., Eval intake, Progress Reviews",
    permissions: {
      Administrator: "R/W/D",
      Superuser: "R/W/D",
      Coordinator: "R/W",
      Viewer: "R",
    },
  },
  {
    id: "program-tasks",
    label: "Program Tasks",
    permissions: {
      Administrator: "R/W/D",
      Superuser: "R/W/D",
      Coordinator: "R/W",
      Viewer: "R",
    },
  },
  {
    id: "program-notes",
    label: "Program Notes",
    permissions: {
      Administrator: "R/W/D",
      Superuser: "R/W/D",
      Coordinator: "R/W",
      Viewer: "R",
    },
  },
  {
    id: "program-documents",
    label: "Program Documents",
    permissions: {
      Administrator: "R/W/D",
      Superuser: "R/W/D",
      Coordinator: "R/W",
      Viewer: "R",
    },
  },
  {
    id: "list-of-values",
    label: "List of Values",
    subtitle: "Manage dropdown lists",
    permissions: {
      Administrator: "R/W/D",
      Superuser: "R",
      Coordinator: "N",
      Viewer: "N",
    },
  },
];

export const ACCESS_LEVEL_DEFINITIONS: AccessLevelDefinition[] = [
  {
    level: "None",
    definition: "The user does not know the data exists.",
    example: "Not knowing AI settings exist",
  },
  {
    level: "Read",
    definition: "The user can view the data but cannot change it.",
    example: "Viewing a participant's address",
  },
  {
    level: "Write",
    definition: "The user can add new data or update existing data.",
    example: "Uploading a document, adding a task, or updating a note.",
  },
  {
    level: "Delete",
    definition: "The user can permanently destroy data.",
    example: "Deleting a person or a progress review record.",
  },
];

export const ROLE_USER_TYPES: RoleUserTypes[] = [
  {
    role: "Administrator",
    userTypes: ["CTS Business System Analysts"],
  },
  {
    role: "Superuser",
    userTypes: [
      "Operations Analysts",
      "Operations Supervisors",
      "Operations Managers",
    ],
  },
  {
    role: "Coordinator",
    userTypes: [
      "Collaborative Court Coordinators",
      "Program Coordinator/Specialists",
    ],
  },
  {
    role: "Viewer",
    userTypes: ["BAT Data Analysts", "BAT Data Scientists"],
  },
];

const ROLE_ALIASES: Record<string, AppRole> = {
  administrator: "Administrator",
  admin: "Administrator",
  super_admin: "Administrator",
  superuser: "Superuser",
  super_user: "Superuser",
  coordinator: "Coordinator",
  viewer: "Viewer",
  read_only: "Viewer",
  readonly: "Viewer",
  cts_business_system_analysts: "Administrator",
  operations_analysts: "Superuser",
  operations_supervisors: "Superuser",
  operations_managers: "Superuser",
  collaborative_court_coordinators: "Coordinator",
  program_coordinator_specialists: "Coordinator",
  bat_data_analysts: "Viewer",
  bat_data_scientists: "Viewer",
};

const AUTH_CAPABILITIES_STORAGE_KEY = "auth_capabilities";

export function normalizeAppRole(roleFromToken?: string | null): AppRole {
  if (!roleFromToken) return "Viewer";
  const normalized = roleFromToken.trim().toLowerCase().replace(/[\s-]+/g, "_");
  return ROLE_ALIASES[normalized] ?? "Viewer";
}

export function getAccessForRow(rowId: string, role: AppRole): AccessValue {
  const capabilities = getStoredAuthorizationCapabilities();
  
  // Extra safety: ensure capabilities and permissions exist
  if (!capabilities || !capabilities.permissions) {
    console.warn(`⚠️ No capabilities for rowId: ${rowId}, using default permissions`);
    const row = ACCESS_RIGHT_ROWS.find((item) => item.id === rowId);
    if (!row) return "N";
    return row.permissions[role];
  }
  
  const backendValue = capabilities.permissions[rowId];
  if (backendValue) {
    return backendValue;
  }

  const row = ACCESS_RIGHT_ROWS.find((item) => item.id === rowId);
  if (!row) return "N";
  return row.permissions[role];
}

export function canRead(rowId: string, role: AppRole): boolean {
  const value = getAccessForRow(rowId, role);
  return value === "R" || value === "R/W" || value === "R/W/D";
}

/**
 * Read access from the STATIC role matrix only (ignores backend per-row
 * overrides). Used for Admin Center visibility so a Super user always sees the
 * Governance & Access areas read-only, even if the backend capability payload
 * omits or zeroes a given area.
 */
export function roleMatrixCanRead(rowId: string, role: AppRole): boolean {
  const row = ACCESS_RIGHT_ROWS.find((item) => item.id === rowId);
  const value = row?.permissions[role] ?? "N";
  return value === "R" || value === "R/W" || value === "R/W/D";
}

export function canWrite(rowId: string, role: AppRole): boolean {
  const value = getAccessForRow(rowId, role);
  return value === "R/W" || value === "R/W/D";
}

export function canDelete(rowId: string, role: AppRole): boolean {
  return getAccessForRow(rowId, role) === "R/W/D";
}

export function canAccessAdminCenter(role: AppRole): boolean {
  return (
    canRead("admin-settings", role) ||
    canRead("care-team-members", role) ||
    canRead("list-of-values", role)
  );
}

export function canManageListOfValues(role: AppRole): boolean {
  return canWrite("list-of-values", role);
}

export function canManageCareTeamDirectory(role: AppRole): boolean {
  return canWrite("care-team-members", role) || canWrite("admin-settings", role);
}

export function canDeleteCareTeamDirectory(role: AppRole): boolean {
  return canDelete("care-team-members", role) || canDelete("admin-settings", role);
}

export function canAccess(
  rowId: string,
  role: AppRole,
  action: AccessAction = "read",
): boolean {
  if (action === "write") return canWrite(rowId, role);
  if (action === "delete") return canDelete(rowId, role);
  return canRead(rowId, role);
}

export function getCurrentUserRole(): AppRole {
  if (typeof window === "undefined") return "Viewer";
  const capabilities = getStoredAuthorizationCapabilities();
  if (capabilities?.role) {
    return capabilities.role;
  }
  const roleFromStorage = sessionStorage.getItem("user_role");
  return normalizeAppRole(roleFromStorage);
}

export function getStoredAuthorizationCapabilities(): AuthorizationCapabilities | null {
  if (typeof window === "undefined") return null;
  const raw = sessionStorage.getItem(AUTH_CAPABILITIES_STORAGE_KEY);
  if (!raw) {
    console.warn("⚠️ No authorization capabilities in storage");
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as AuthorizationCapabilities;
    // Ensure permissions object exists even if empty
    if (!parsed.permissions) {
      console.warn("⚠️ Authorization capabilities missing permissions object, adding empty object");
      parsed.permissions = {};
    }
    return parsed;
  } catch (error) {
    console.error("❌ Failed to parse authorization capabilities:", error);
    return null;
  }
}

export function storeAuthorizationCapabilities(
  capabilities: AuthorizationCapabilities,
): void {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(
    AUTH_CAPABILITIES_STORAGE_KEY,
    JSON.stringify(capabilities),
  );
  sessionStorage.setItem("user_role", capabilities.role);
}

export function clearAuthorizationCapabilities(): void {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(AUTH_CAPABILITIES_STORAGE_KEY);
}